import { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material'
import { usePOTracking } from '@/hooks/usePOTracking'
import { OverviewChart } from '@/components/PoTracking/OverviewChart'
import { POTable } from '@/components/PoTracking/POTable'
import type { ForecastConfidence, ForecastObjective, PurchaseOrderWithUsage } from '@/types/po'

interface KPI {
  label: string
  value: string
  color: 'primary' | 'success' | 'warning' | 'error' | 'default'
}

const ADD_YEAR_VALUE = -1
const lineTypeOptions = [
  { value: 'linear', label: 'Linear' },
  { value: 'monotone', label: 'Monotone' },
  { value: 'step', label: 'Step' },
  { value: 'stepAfter', label: 'Step After' },
] as const

type LineTypeOption = typeof lineTypeOptions[number]['value']
const FORECAST_OBJECTIVE: ForecastObjective = 'balanced'

function formatModelName(model: string): string {
  if (model === 'baseline_avg_nonzero') return 'Baseline'
  return model.toUpperCase()
}

function confidenceChipColor(confidence: ForecastConfidence): 'success' | 'warning' | 'error' {
  if (confidence === 'high') return 'success'
  if (confidence === 'medium') return 'warning'
  return 'error'
}

export default function PoTrackingPage() {
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [filteredPOId, setFilteredPOId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPO, setEditingPO] = useState<PurchaseOrderWithUsage | null>(null)
  const [customYears, setCustomYears] = useState<number[]>([])
  const [lineType, setLineType] = useState<LineTypeOption>('stepAfter')

  const { useYears, usePOsWithUsage, useForecast, createPO, updatePO, deletePO } = usePOTracking()

  const { data: years = [] } = useYears()
  const { data: pos = [], isLoading: posLoading, refetch: refetchPOs } = usePOsWithUsage(selectedYear)
  const { data: forecast, isLoading: forecastLoading } = useForecast(
    selectedYear,
    filteredPOId ?? undefined,
    FORECAST_OBJECTIVE
  )

  // Calculate KPIs
  const kpis: KPI[] = useMemo(() => {
    const totalPot = pos.reduce((sum, po) => sum + po.valueTotalEur, 0)
    const totalUsed = pos.reduce((sum, po) => sum + po.valueUsed, 0)
    const totalRemaining = totalPot - totalUsed
    const totalOverrun = pos.reduce((sum, po) => sum + Math.max(0, po.valueUsed - po.valueTotalEur), 0)

    const formatEUR = (val: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)

    return [
      { label: 'Total PO Budget', value: formatEUR(totalPot), color: 'primary' as const },
      { label: 'Total Used', value: formatEUR(totalUsed), color: 'success' as const },
      { label: 'Remaining', value: formatEUR(Math.max(0, totalRemaining)), color: totalOverrun > 0 ? 'warning' as const : 'default' as const },
      { label: 'Overrun', value: formatEUR(totalOverrun), color: totalOverrun > 0 ? 'error' as const : 'default' as const },
    ]
  }, [pos])

  const handleCreatePO = () => {
    setEditingPO(null)
    setDialogOpen(true)
  }

  const handleEditPO = (po: PurchaseOrderWithUsage) => {
    setEditingPO(po)
    setDialogOpen(true)
  }

  const handleDeletePO = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this PO? Linked invoices will be unassigned.')) {
      await deletePO.mutateAsync(id)
      refetchPOs()
    }
  }

  const handleSavePO = async (data: { poNumber: string; description: string | null; valueTotalEur: number; year: number }) => {
    if (editingPO) {
      await updatePO.mutateAsync({ id: editingPO.id, data })
    } else {
      await createPO.mutateAsync(data)
    }
    setDialogOpen(false)
    refetchPOs()
  }

  const handleFilterByPO = (poId: string | null) => {
    setFilteredPOId(poId)
  }

  const currentYear = new Date().getFullYear()

  // Add current year to list if not present
  const availableYears = useMemo(() => {
    const yearSet = new Set(years)
    yearSet.add(currentYear)
    customYears.forEach((year) => yearSet.add(year))
    return Array.from(yearSet).sort((a, b) => b - a)
  }, [years, currentYear, customYears])

  const handleAddYear = (onAdded?: (year: number) => void) => {
    const input = window.prompt('Enter year (e.g. 2027)')
    if (!input) return
    const parsed = Number(input)
    if (!Number.isInteger(parsed) || parsed < 2000 || parsed > 2100) {
      alert('Please enter a valid year')
      return
    }
    setCustomYears((prev) => (prev.includes(parsed) ? prev : [...prev, parsed]))
    setSelectedYear(parsed)
    if (onAdded) onAdded(parsed)
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          PO Tracking
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreatePO}>
          New PO
        </Button>
      </Box>

      {/* Year Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle1">Year:</Typography>
          <Select
            value={selectedYear}
            onChange={(e) => {
              const value = Number(e.target.value)
              if (value === ADD_YEAR_VALUE) {
                handleAddYear()
                return
              }
              setSelectedYear(value)
            }}
            sx={{ minWidth: 120 }}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
            <MenuItem value={ADD_YEAR_VALUE}>
              + Add New Year
            </MenuItem>
          </Select>

          {filteredPOId && (
            <Chip
              label="Filtered by PO"
              onDelete={() => setFilteredPOId(null)}
              color="primary"
              deleteIcon={<TrendingUpIcon />}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="textSecondary">
            {pos.length} PO{pos.length !== 1 ? 's' : ''} in {selectedYear}
          </Typography>
        </Stack>
      </Paper>

      {/* KPI Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} useFlexGap>
        {kpis.map((kpi) => (
          <Paper key={kpi.label} sx={{ flex: 1, p: 2, minWidth: 200 }}>
            <Typography variant="caption" color="textSecondary">
              {kpi.label}
            </Typography>
            <Typography variant="h6" color={kpi.color}>
              {kpi.value}
            </Typography>
          </Paper>
        ))}
      </Stack>

      {/* Overview and Forecast Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6">
            Overview and Forecast
            {filteredPOId && ` (Filtered)`}
          </Typography>
          {forecast && (
            <>
              <Chip
                size="small"
                variant="outlined"
                label={`Model: ${formatModelName(forecast.forecastMeta.model)}`}
              />
              <Chip
                size="small"
                color={confidenceChipColor(forecast.forecastMeta.confidence)}
                variant="outlined"
                label={`Confidence: ${forecast.forecastMeta.confidence}`}
              />
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="line-type-label">Line</InputLabel>
            <Select
              labelId="line-type-label"
              value={lineType}
              label="Line"
              onChange={(e) => setLineType(e.target.value as LineTypeOption)}
            >
              {lineTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {forecastLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : forecast ? (
          <OverviewChart data={forecast} lineType={lineType} />
        ) : (
          <Alert severity="info">No forecast data available for the selected period.</Alert>
        )}
      </Paper>

      {/* PO Table */}
      <POTable
        pos={pos}
        loading={posLoading}
        onEdit={handleEditPO}
        onDelete={handleDeletePO}
        onFilter={handleFilterByPO}
        filteredPOId={filteredPOId}
      />

      {/* Create/Edit Dialog */}
      <PODialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSavePO}
        po={editingPO}
        availableYears={availableYears}
        onAddYear={handleAddYear}
      />
    </Box>
  )
}

interface PODialogProps {
  open: boolean
  onClose: () => void
  onSave: (data: { poNumber: string; description: string | null; valueTotalEur: number; year: number }) => void
  po: PurchaseOrderWithUsage | null
  availableYears: number[]
  onAddYear: (onAdded?: (year: number) => void) => void
}

function PODialog({ open, onClose, onSave, po, availableYears, onAddYear }: PODialogProps) {
  const [poNumber, setPONumber] = useState('')
  const [description, setDescription] = useState('')
  const [valueTotalEur, setValueTotalEur] = useState('')
  const [year, setYear] = useState<number>(new Date().getFullYear())

  // Reset form when PO changes or dialog opens
  useEffect(() => {
    if (open) {
      if (po) {
        setPONumber(po.poNumber)
        setDescription(po.description || '')
        setValueTotalEur(po.valueTotalEur.toString())
        setYear(po.year)
      } else {
        setPONumber('')
        setDescription('')
        setValueTotalEur('')
        setYear(new Date().getFullYear())
      }
    }
  }, [open, po])

  const handleSave = () => {
    const value = parseFloat(valueTotalEur)
    if (!poNumber.trim()) {
      alert('PO Number is required')
      return
    }
    if (isNaN(value) || value < 0) {
      alert('Please enter a valid amount')
      return
    }

    onSave({
      poNumber: poNumber.trim(),
      description: description.trim() || null,
      valueTotalEur: value,
      year,
    })

    // Reset form
    setPONumber('')
    setDescription('')
    setValueTotalEur('')
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{po ? 'Edit PO' : 'Create New PO'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="PO Number"
            value={poNumber}
            onChange={(e) => setPONumber(e.target.value)}
            fullWidth
            autoFocus
            placeholder="e.g. PO-2024-001"
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Optional description"
          />

          <TextField
            label="Total Value (EUR)"
            value={valueTotalEur}
            onChange={(e) => setValueTotalEur(e.target.value)}
            fullWidth
            type="number"
            InputProps={{
              startAdornment: <Box sx={{ mr: 1 }}>€</Box>,
            }}
          />

          <FormControl fullWidth>
            <InputLabel>Year</InputLabel>
            <Select
              value={year}
              onChange={(e) => {
                const value = Number(e.target.value)
                if (value === ADD_YEAR_VALUE) {
                  onAddYear((newYear) => setYear(newYear))
                  return
                }
                setYear(value)
              }}
              label="Year"
            >
              {availableYears.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
              <MenuItem value={ADD_YEAR_VALUE}>
                + Add New Year
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {po ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
